// Organization Production Grant (ADR-048 / common!98 specs/19 §1) admin API layer.
//
// The grant is an organization-only, versioned production authorization that is
// deliberately SEPARATE from account tier / org approval: an approved enterprise
// org still has no production authority until this grant is `active` and carries
// the operation's scope. Contract sources:
//   - common!98 specs/19_platform_lifecycle_governance.md (AC-PLG-001..004)
//   - common contracts/platform_lifecycle_policy.json (developer_access.production_grant)
//   - restful feat/model-lifecycle-policy src/api/openapi.yaml
//     PUT /v3/admin/dev/orgs/{org_id}/production-grant → ProductionGrant / ProductionGrantMutation
//
// NOTE: the restful backend (restful!229-!232) is still Draft / not deployed at
// time of writing. This layer is written strictly against the ratified OpenAPI
// schema; live wiring + integration verification lands once the backend deploys.

import { ApiError, apiFetch } from '../../lib/api';

// ── state machine (ADR-048; domain/developer.go) ──
export type ProductionGrantState = 'applied' | 'active' | 'suspended' | 'revoked';

// ── fixed scope vocabulary — invented/broadened permissions are rejected ──
export type ProductionScope =
  | 'production_aid'
  | 'factory_credential_export'
  | 'stable_firmware_activation'
  | 'production_client_app';

// ── locked ADR-048 approval checklist (6 items; active requires all true) ──
export type ProductionChecklistKey =
  | 'commercial_agreement'
  | 'security_contact'
  | 'support_contact'
  | 'model_review'
  | 'firmware_signing_readiness'
  | 'factory_identity_readiness';

/** Full authority row as returned by the admin read/write endpoints. */
export interface ProductionGrant {
  state: ProductionGrantState;
  scopes: ProductionScope[];
  checklist_version: string;
  checklist: Partial<Record<ProductionChecklistKey, boolean>>;
  version: number;
  applied_by?: string;
  applied_at?: number;
  granted_by?: string;
  granted_at?: number;
  suspended_by?: string;
  suspended_at?: number;
  revoked_by?: string;
  revoked_at?: number;
  last_actor: string;
  last_reason: string;
  last_case_id: string;
  updated_at: number;
}

/**
 * PUT body. `expected_version` is the optimistic lock — send the version you
 * read (0 for the first `applied`). scopes/checklist are only meaningful for
 * applied/active (suspend/revoke preserve the prior authority) but the server
 * still requires checklist_version + reason + case_id on every mutation.
 */
export interface ProductionGrantMutationInput {
  state: ProductionGrantState;
  scopes?: ProductionScope[];
  checklist?: Partial<Record<ProductionChecklistKey, boolean>>;
  checklist_version: string;
  expected_version: number;
  reason: string;
  case_id: string;
}

// ── ordered vocabularies + zh-TW labels (single source for the panel) ──
export const PRODUCTION_SCOPES: ProductionScope[] = [
  'production_aid',
  'factory_credential_export',
  'stable_firmware_activation',
  'production_client_app',
];

export const PRODUCTION_SCOPE_LABELS: Record<ProductionScope, string> = {
  production_aid: 'Production AID 配發',
  factory_credential_export: '工廠憑證匯出',
  stable_firmware_activation: 'Stable 韌體啟用',
  production_client_app: 'Production 用戶端 App',
};

export const PRODUCTION_CHECKLIST_KEYS: ProductionChecklistKey[] = [
  'commercial_agreement',
  'security_contact',
  'support_contact',
  'model_review',
  'firmware_signing_readiness',
  'factory_identity_readiness',
];

export const PRODUCTION_CHECKLIST_LABELS: Record<ProductionChecklistKey, string> = {
  commercial_agreement: '商務合約',
  security_contact: '安全聯絡人',
  support_contact: '支援聯絡人',
  model_review: 'Model 審核',
  firmware_signing_readiness: '韌體簽章就緒',
  factory_identity_readiness: '工廠身分就緒',
};

export const PRODUCTION_GRANT_STATE_LABELS: Record<ProductionGrantState, string> = {
  applied: '已申請',
  active: '已啟用',
  suspended: '已停權',
  revoked: '已撤銷',
};

/** Semantic tone for a grant state (maps to global .pill-* / .tone-* classes). */
export function productionGrantTone(state: ProductionGrantState | undefined): 'good' | 'warn' | 'danger' | 'info' | 'neutral' {
  switch (state) {
    case 'active':
      return 'good';
    case 'applied':
      return 'info';
    case 'suspended':
      return 'warn';
    case 'revoked':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Legal lifecycle transitions — a faithful mirror of the backend
 * `validProductionGrantTransition` (service/productiongrant.go) so the console
 * only OFFERS moves the server will accept. Same-state (applied/active/suspended)
 * is a scope/checklist refresh; revoked→revoked is disallowed.
 */
export function canTransition(from: ProductionGrantState | undefined, to: ProductionGrantState): boolean {
  if (!from) return to === 'applied';
  if (from === to) return from !== 'revoked';
  switch (from) {
    case 'applied':
      return to === 'active' || to === 'revoked';
    case 'active':
      return to === 'suspended' || to === 'revoked';
    case 'suspended':
      return to === 'active' || to === 'revoked';
    case 'revoked':
      return to === 'applied';
    default:
      return false;
  }
}

/** True only for an `active` grant carrying the exact scope (mirror of HasScope). */
export function grantHasScope(grant: ProductionGrant | null | undefined, scope: ProductionScope): boolean {
  return !!grant && grant.state === 'active' && grant.scopes.includes(scope);
}

/**
 * PUT /v3/admin/dev/orgs/{org_id}/production-grant — apply / activate / suspend /
 * revoke one audited, optimistic lifecycle mutation.
 */
export async function setProductionGrant(orgId: string, input: ProductionGrantMutationInput): Promise<ProductionGrant> {
  const res = await apiFetch<ProductionGrant>(`/v3/admin/dev/orgs/${encodeURIComponent(orgId)}/production-grant`, {
    method: 'PUT',
    body: input,
  });
  return res.data;
}

export interface ProductionGrantErrorInfo {
  text: string;
  /** A 409 optimistic-lock / stale-transition — the caller should reload before retry. */
  conflict: boolean;
}

/**
 * Operator-facing message for a failed grant mutation, keyed to the DISTINCT
 * backend codes (handler/common.go). 409s (CAS conflict / illegal transition)
 * are flagged so the panel can offer reload+retry instead of blindly overwriting
 * another admin's change. 403 hard-gate reasons are surfaced verbatim by code.
 */
export function productionGrantErrorMessage(e: unknown): ProductionGrantErrorInfo {
  if (!(e instanceof ApiError)) return { text: '操作失敗，請稍後再試。', conflict: false };
  if (e.status === 409) {
    if (e.code === 'INVALID_PRODUCTION_GRANT_TRANSITION') {
      return {
        text: '狀態已變動：此狀態轉換在目前授權狀態下已不合法。請重新載入後再試。（INVALID_PRODUCTION_GRANT_TRANSITION）',
        conflict: true,
      };
    }
    return {
      text: '此 Production Grant 已被其他管理員修改（樂觀鎖衝突）。請重新載入取得最新版本後再試，以免覆寫他人的變更。（CONFLICT）',
      conflict: true,
    };
  }
  if (e.status === 403) {
    switch (e.code) {
      case 'PRODUCTION_GRANT_REQUIRED':
        return {
          text: '生產操作遭硬門攔截：需要 active 且含對應 scope 的 Production Grant。個人工作區、缺授權、已停權或已撤銷皆會被擋。（PRODUCTION_GRANT_REQUIRED）',
          conflict: false,
        };
      case 'ADMIN_REGION_MISMATCH':
        return { text: '區域不符：此組織隸屬其他 region，僅該 region 的管理員可操作。（ADMIN_REGION_MISMATCH）', conflict: false };
      case 'FORBIDDEN':
      default:
        // e.g. "organization not approved" — activate is refused until the org
        // itself is approved; enterprise/org approval never implies this grant.
        return { text: `伺服器拒絕此操作：${e.message}（${e.code}）`, conflict: false };
    }
  }
  if (e.status === 422 && e.code === 'INVALID_PRODUCTION_GRANT') {
    return {
      text: '無效的 Production Grant：狀態、scope 或核准檢核清單不完整。啟用（active）需 6 項檢核全數通過且至少 1 個 scope。（INVALID_PRODUCTION_GRANT）',
      conflict: false,
    };
  }
  if (e.status === 400 && e.code === 'INVALID_REQUEST') {
    return { text: '請填寫原因與案件編號（reason／case_id 皆為稽核必填）。', conflict: false };
  }
  if (e.status === 404) {
    return { text: '找不到此組織（NOT_FOUND）。', conflict: false };
  }
  return { text: `${e.message}（${e.code}）`, conflict: false };
}
